import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { blogAPI, uploadFile } from '../services/api'
import { useNotification } from '../context/NotificationContext'
import { getErrorMessage, isValidInstagramUrl, getImageUrl } from '../utils/helpers'

const BlogForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showSuccess, showError } = useNotification()
  const [loading, setLoading] = useState(!!id)
  const [post, setPost] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: '',
    image: '',
    video_url: '',
    instagram_url: '',
    published: false,
  })

  useEffect(() => {
    if (id) {
      fetchPost()
    }
  }, [id])

  const fetchPost = async () => {
    try {
      const response = await blogAPI.getOne(id)
      setPost(response.data.post)
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    
    try {
      const data = {
        title: formData.get('title'),
        excerpt: formData.get('excerpt'),
        content: formData.get('content'),
        author: formData.get('author'),
        video_url: formData.get('video_url'),
        published: formData.get('published') === 'on',
        image: post.image || null,
      }

      const imageFile = formData.get('image')
      if (imageFile && imageFile.size > 0) {
        const uploadRes = await uploadFile(imageFile)
        data.image = uploadRes.data.filename || uploadRes.data.image
      }

      if (id) {
        await blogAPI.update(id, data)
        showSuccess('Post updated.')
      } else {
        await blogAPI.create(data)
        showSuccess('Post created.')
      }
      
      navigate('/admin/blog')
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleReelSubmit = async (e) => {
    e.preventDefault()
    const url = e.target.instagram_url.value

    if (url && !isValidInstagramUrl(url)) {
      showError('That is not a valid Instagram Reel URL. Use a link like https://www.instagram.com/reel/ABC123xyz/')
      return
    }

    try {
      await blogAPI.updateReel(id, url)
      showSuccess('Reel saved successfully.')
      setPost({ ...post, instagram_url: url })
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  if (loading) return <div className="admin-page">Loading...</div>

  return (
    <div className="admin-page">
      <div className="page-head">
        <h2 className="admin-title">{id ? 'Edit' : 'New'} Post</h2>
        <Link to="/admin/blog" className="btn btn-outline-secondary">
          <i className="fa fa-arrow-left"></i> Back to Blog
        </Link>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="panel">
            <div className="panel-head">Post Details</div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input type="text" name="title" className="form-control" defaultValue={post.title} required />
              </div>
              <div className="form-group">
                <label>Excerpt</label>
                <textarea name="excerpt" className="form-control" rows="3" defaultValue={post.excerpt}></textarea>
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea name="content" className="form-control textarea-tall" defaultValue={post.content}></textarea>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Author</label>
                    <input type="text" name="author" className="form-control" defaultValue={post.author} placeholder="Hair Team" />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Video URL (YouTube/Vimeo)</label>
                    <input type="url" name="video_url" className="form-control" defaultValue={post.video_url} placeholder="https://youtube.com/watch?v=..." />
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Image</label>
                <input type="file" name="image" className="form-control-file" accept="image/*" />
                {post.image && (
                  <div className="file-preview">
                    <span>Current image</span>
                    <img src={getImageUrl(post.image)} alt="Current" />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="checkbox-inline">
                  <input type="checkbox" name="published" defaultChecked={post.published} /> Published
                </label>
              </div>
              <button type="submit" className="btn btn-gold btn-block">
                {id ? 'Update' : 'Create'} Post
              </button>
            </form>
          </div>
        </div>

        {id && (
          <div className="col-lg-4">
            <div className="panel">
              <div className="panel-head">Instagram Reel</div>
              <form onSubmit={handleReelSubmit}>
                <div className="form-group">
                  <label>Instagram Reel URL</label>
                  <input 
                    type="url" 
                    name="instagram_url" 
                    className="form-control" 
                    defaultValue={post.instagram_url}
                    placeholder="https://www.instagram.com/reel/..."
                  />
                </div>
                <button type="submit" className="btn btn-gold btn-block">Save Reel</button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BlogForm
